
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Product } from '@/types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { Calendar, Mail, Image, FileText } from 'lucide-react';
import { useState } from 'react';

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const [imageError, setImageError] = useState(false);
  
  // Check if the current date is after expiration date
  const now = new Date();
  const licenseStartDate = new Date(product.licenseStartDate);
  const expirationDate = new Date(product.expirationDate);
  
  // Determine if the product is active based on current date compared to start and end dates
  const isExpired = now > expirationDate;
  const isActive = now >= licenseStartDate && now <= expirationDate;
  
  // Format dates for display in French
  const formattedStartDate = format(licenseStartDate, 'dd MMM yyyy', { locale: fr });
  const formattedEndDate = format(expirationDate, 'dd MMM yyyy', { locale: fr });
  
  // Determine days until expiration (or days since expiration)
  const diffTime = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Determine status color: green for active, orange for near expiration (30 days), red for expired
  const isNearExpiration = diffDays <= 30 && diffDays > 0;
  const status = diffDays <= 0 ? 'expired' : diffDays <= 30 ? 'near-expiration' : 'active';
  
  // Get the first image URL if multiple are provided
  const getFirstImageUrl = () => {
    if (!product.imagePath) return null;
    const urls = product.imagePath.split(',').map(url => url.trim()).filter(url => url);
    return urls.length > 0 ? urls[0] : null;
  };
  
  const imageUrl = getFirstImageUrl();
  
  const getExpiryMessage = () => {
    if (diffDays > 0) {
      return `Expire dans ${diffDays} jour${diffDays !== 1 ? 's' : ''}`;
    } else if (diffDays < 0) {
      return `Expiré depuis ${Math.abs(diffDays)} jour${Math.abs(diffDays) !== 1 ? 's' : ''}`;
    } else {
      return 'Expire aujourd\'hui';
    }
  };

  const getCardStyles = () => {
    switch (status) {
      case 'expired':
        return 'border-destructive/40 bg-destructive/10';
      case 'near-expiration':
        return 'border-orange-500/40 bg-orange-50';
      default:
        return 'border-green-500/40 bg-green-50';
    }
  };

  const getFooterStyles = () => {
    switch (status) {
      case 'expired':
        return 'bg-destructive/20 text-destructive-foreground';
      case 'near-expiration':
        return 'bg-orange-500/20 text-orange-800';
      default:
        return 'bg-green-500/20 text-green-800';
    }
  };

  const getBadgeVariant = () => {
    return status === 'expired' ? "destructive" : 
           status === 'near-expiration' ? "default" : 
           "outline";
  };

  const getBadgeStyles = () => {
    if (status === 'active') {
      return 'bg-green-500 text-white border-green-500';
    } else if (status === 'near-expiration') {
      return 'bg-orange-500 text-white border-orange-500';
    }
    return '';
  };

  const handleImageError = () => {
    console.log("Image error for:", imageUrl);
    setImageError(true);
  };

  // Validate if the URL is a proper image URL
  const isValidImageUrl = (url: string | null): boolean => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <Card className={`overflow-hidden transition-all hover:shadow-md ${getCardStyles()}`}>
      <CardHeader className="p-4 pb-0">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold line-clamp-1">{product.name}</h3>
          <Badge variant={getBadgeVariant()} className={getBadgeStyles()}>
            {status === 'expired' ? 'Expiré' : 
             status === 'near-expiration' ? 'Bientôt expiré' : 'Actif'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="relative h-40 mb-3 bg-muted flex items-center justify-center overflow-hidden rounded-md">
          {imageUrl && isValidImageUrl(imageUrl) && !imageError ? (
            <img 
              src={imageUrl} 
              alt={product.name} 
              className="object-contain w-full h-full"
              onError={handleImageError}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <Image className="h-10 w-10 mb-2 opacity-40" />
              <span>Pas d'image</span>
            </div>
          )}
        </div>
        
        {/* Description section */}
        {product.description && (
          <div className="mb-3">
            <div className="flex items-start text-muted-foreground">
              <FileText className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm line-clamp-2">{product.description}</p>
            </div>
          </div>
        )}
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-muted-foreground">
            <Mail className="h-4 w-4 mr-2" />
            <span className="truncate">{product.ipAddress}</span>
          </div>
          <div className="flex items-center text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            <span>Du: {formattedStartDate}</span>
          </div>
          <div className="flex items-center text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            <span>Au: {formattedEndDate}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className={`px-4 py-3 bg-opacity-20 text-sm font-medium ${getFooterStyles()}`}>
        {getExpiryMessage()}
      </CardFooter>
    </Card>
  );
};

export default ProductCard;
